import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import { Button, Card, Chip, Link, Stack, Typography } from "@mui/material";
import React from "react";
import { Link as RouterLink } from "react-router";
import { orderService } from "../../../Core/Api/services";
import { DbOrderBasketItemInfos } from "../../../Core/Api/Types/db-order-basket-item-infos";
import { DbOrderItemInfos } from "../../../Core/Api/Types/db-order-item-infos";
import { MissingAssetDetails } from "../../../Core/Api/Types/missing-asset-details";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { routePaths } from "../../../Router/router";
import { isDbOrderBasketItemInfos } from "../../../Utils/helpers";
import OrderProductIcon from "../../Misc/OrderProductIcon/order-product-icon";
import { ModalContext } from "../../Modals";
import ModalOrderBundlemakerInspector, {
  ModalOrderBundlemakerInspectorProps,
} from "../../Modals/ModalOrderBundlemakerInspector/modal-order-bundlemaker-inspector";
import { useTranslation } from "react-i18next";

export interface MissingAssetData {
  type: string;
  message: string;
  iteration: number;
  objectIds: number[] | null;
}

const OrderMissingAssetCard = (props: { item: DbOrderItemInfos | DbOrderBasketItemInfos }) => {
  const { t } = useTranslation();
  const { item } = props;

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [missingAssetsCandidates, setMissingAssetsCandidates] = React.useState<MissingAssetData[]>([]);

  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const getCandidateAssets = (
    type: string,
    iteration: number,
    missingAssetsCandidates: MissingAssetDetails[]
  ): number[] => {
    let array: number[] = [];

    if (item.doable) return [];

    for (let i = 0; i < missingAssetsCandidates?.length; ++i) {
      if (missingAssetsCandidates[i].issues === null) continue;

      for (let y = 0; y < missingAssetsCandidates[i]?.issues!.length; y++) {
        let find = missingAssetsCandidates[i]?.issues!.find((x) => x.item!.id === item.id);

        if (find) {
          let issue = find.checks?.find((x) => x.check === type && x.iteration === iteration);

          if (issue) array.push(missingAssetsCandidates[i]?.assetId!);
        }
      }
    }
    return array;
  };

  const itemType: OrderProductType = React.useMemo(() => {
    if (isDbOrderBasketItemInfos(item)) {
      return item.selectedProduct!.type!;
    } else {
      return item.type!;
    }
  }, [item]);

  React.useEffect(() => {
    if (!item!.doable) {
      setIsLoading(true);
      let ma: MissingAssetData[] = [];

      if (isDbOrderBasketItemInfos(item)) {
        let checks: any[] = [];
        //  item.selectedProduct?.settings?.tgop?.checks;
        // if (!checks && item.selectedProduct?.settings?.checks) checks = item.selectedProduct?.settings?.checks;

        // TODO: get checks on switch orderProductType
        switch (item.selectedProduct?.type) {
          case OrderProductType.TranscodeByProfile:
            checks = item.selectedProduct?.settings?.checks;
            break;
          case OrderProductType.TranscodeGeneric:
          case OrderProductType.TranscodeGenericSingleFile:
            checks = item.selectedProduct?.settings?.tgop?.checks;
            break;
        }

        checks
          ?.filter((x) => x.missingAssets)
          .map((check) => {
            ma.push({
              type: check.check,
              message: check.message,
              iteration: check.iteration,
              objectIds: check.concernedAssetIds || [],
            });
          });

        setMissingAssetsCandidates(ma);
        setIsLoading(false);
      } else {
        if (item.relatedTitleId) {
          orderService.GetMissingAssetsCandidatesByTitleId(item.relatedTitleId!).then((response) => {
            if (response.assetsAndIssues !== null) {
              console.log("loaded GetMissingAssetsCandidatesByTitleId() ok");

              let checks = item.product?.settings?.tgop?.checks;
              if (!checks && item.product?.settings?.checks) checks = item.product?.settings?.checks;
              checks
                ?.filter((x) => x.missingAssets)
                .map((check) => {
                  ma.push({
                    type: check.check,
                    message: check.message,
                    iteration: check.iteration,
                    objectIds:
                      response.assetsAndIssues !== null
                        ? getCandidateAssets(check.check, check.iteration, response.assetsAndIssues!)
                        : [],
                  });
                });
              setMissingAssetsCandidates(ma);
            }
            setIsLoading(false);
          });
        }
      }
    }
  }, []);

  return item!.doable ? null : (
    <Card variant="outlined" sx={{ p: 1.5, mt: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" spacing={2} mb={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <OrderProductIcon type={itemType} />
          <Typography>{t("menu:item.missingAssets")}</Typography>
        </Stack>
        <Button
          variant="icon"
          size="small"
          startIcon={<BuildCircleOutlinedIcon fontSize="small" />}
          component={RouterLink}
          to={routePaths.ingestNewRequest.path}
        >
          {t("common:request")}
        </Button>
      </Stack>

      {itemType !== OrderProductType.TranscodeBundleByProfile &&
        !isLoading &&
        missingAssetsCandidates.map((check, index) => (
          <Stack direction="row" justifyContent="space-between" spacing={2} mt={1} key={index}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.tier">
                {check.message}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography>
                {check?.objectIds &&
                  check?.objectIds.length > 0 &&
                  check?.objectIds?.map((objectId) => (
                    <Link
                      component={RouterLink}
                      to={`${routePaths.asset.path}/${objectId}`}
                      target="_blank"
                      underline="hover"
                      variant="body1"
                      ml={1}
                    >
                      {objectId}
                    </Link>
                  ))}
                {!check?.objectIds || (check?.objectIds.length === 0 && <Chip label={t("chip:label.noFoundAsset")} />)}
              </Typography>
            </Stack>
          </Stack>
        ))}
      {itemType !== OrderProductType.TranscodeBundleByProfile && isLoading && (
        <Stack direction="row" justifyContent="space-between" spacing={2} mt={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography>{GlobalConst.LOADING_TEXT}</Typography>
          </Stack>
        </Stack>
      )}

      {itemType === OrderProductType.TranscodeBundleByProfile && (
        <Button
          variant="outlined"
          size="small"
          fullWidth
          onClick={() => {
            openModal &&
              openModal<ModalOrderBundlemakerInspectorProps>(ModalOrderBundlemakerInspector, {
                closeModal: () => false,
                item: item,
              });
          }}
        >
          {t("button:openBundleInspector")}
        </Button>
      )}
    </Card>
  );
};

export default OrderMissingAssetCard;
